import React, { useState } from 'react';
import { confirmSignIn } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  background: url('/images/hero-2.jpeg') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    padding: 0 var(--spacing-md);
    min-height: 100vh;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  color: white;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-xl);
  }
`;

const PasswordCard = styled.div`
  position: relative;
  z-index: 2;
  width: 400px;
  background: rgba(255, 255, 255, 0.95);
  padding: var(--spacing-xl);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: var(--spacing-lg);
  }
  
  @media (max-width: 480px) {
    padding: var(--spacing-md);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const InfoText = styled.p`
  color: var(--text-secondary);
  text-align: center;
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
`;

const NewPasswordRequired = ({ challengeResponse, onPasswordSet }) => {
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await confirmSignIn({
        challengeResponse: newPassword,
      });

      onPasswordSet();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error setting new password:', error);
      setError(error.message);
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <h1>Set Your Password</h1>
          <p>Create a secure password to protect your account and access your personalized learning experience.</p>
        </HeroContent>
        
        <PasswordCard>
          <h2 style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>Create New Password</h2>
          <Form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <InfoText>
              Please create a strong password for your account
            </InfoText>
            
            <Input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            
            <Button type="submit">
              Set Password & Continue
            </Button>
          </Form>
        </PasswordCard>
      </HeroSection>
    </Container>
  );
};

export default NewPasswordRequired;